import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, Zap, Wallet } from 'lucide-react';
import { Link } from 'react-router';
import { useAuth } from '../../../core/hooks/useAuth';
import type { WidgetProps } from '../../../core/types/widget.types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface Stats {
    totalCharges: number;
    totalKwh: number;
    totalRevenueCents: number;
    pendingCount: number;
    invoicedCount: number;
}

export function WallboxStatsWidget({ widgetId: _ }: WidgetProps) {
    const { isAdmin } = useAuth();

    const { data: statsData, isLoading: statsLoading } = useQuery<Stats>({
        queryKey: ['wallbox-stats'],
        queryFn: async () => {
            const response = await axios.get(`${API_URL}/wallbox/charges/stats/summary`, {
                withCredentials: true,
            });
            return response.data.stats as Stats;
        },
    });

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(cents / 100);
    };

    if (statsLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-full">
                {[...Array(2)].map((_, i) => (
                    <div key={i} className="animate-pulse bg-gray-50 rounded-2xl h-24"></div>
                ))}
            </div>
        );
    }

    // Prepare data (defaults to 0)
    const totalCharges = statsData?.totalCharges || 0;
    const totalKwh = statsData?.totalKwh || 0;
    const totalRevenue = statsData?.totalRevenueCents || 0;
    const pendingCount = statsData?.pendingCount || 0;
    // const invoicedCount = statsData?.invoicedCount || 0;

    return (
        <div className="h-full flex flex-col space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                {/* Stats Row 1: Charges & Energy */}
                <div className="bg-primary-50/30 rounded-2xl p-4 border border-primary-50 flex items-center gap-4 transition-all hover:bg-primary-50/50">
                    <div className="w-10 h-10 rounded-xl bg-primary text-white flex items-center justify-center shadow-lg shadow-primary/20">
                        <Zap size={20} />
                    </div>
                    <div className="flex-1">
                        <div className="text-xl font-black text-charcoal">{totalCharges} <span className="text-sm font-normal text-gray-500">Ladungen</span></div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Gesamtenergie</span>
                            <span className="text-[10px] font-bold text-primary">{totalKwh.toFixed(1)} kWh</span>
                        </div>
                    </div>
                </div>

                {/* Stats Row 2: Revenue */}
                <div className="bg-gold-50/30 rounded-2xl p-4 border border-gold-50 flex items-center gap-4 transition-all hover:bg-gold-50/50">
                    <div className="w-10 h-10 rounded-xl bg-gold-600 text-white flex items-center justify-center shadow-lg shadow-gold-600/20">
                        <Wallet size={20} />
                    </div>
                    <div className="flex-1">
                        <div className="text-xl font-black text-charcoal">{formatCurrency(totalRevenue)}</div>
                        <div className="flex items-center justify-between mt-0.5">
                            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Umsatz</span>
                            <span className={`text-[10px] font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                {pendingCount > 0 ? `${pendingCount} Offen` : 'Alles abgerechnet'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pending warning */}
            {pendingCount > 0 && isAdmin && (
                <div className="p-3 bg-amber-50 rounded-xl flex items-start gap-3 border border-amber-100/50">
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-charcoal">
                                {pendingCount} offene Ladevorgänge
                            </p>
                            <Link
                                to="/wallbox/invoices"
                                className="text-[10px] font-bold text-primary hover:text-primary-700 bg-white px-2 py-1 rounded-sm border border-primary-100 transition-colors"
                            >
                                Jetzt abrechnen →
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
